import React, { createContext, useEffect, useReducer, useState, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { sessionAuth, subscribeSession } from 'auth/client';
import { Alert, Button } from '@mui/material';
import { clearIdentity, currentIdentity, refreshIdentity, subscribeIdentity } from 'auth/identity';
import { hasCompanyPermission } from 'utils/company-permissions';
import { SessionTokens } from 'types/session';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project import
import Loader from 'components/Loader';
import { AuthProps, JWTContextType, UserProfile } from 'types/auth';

import api from 'api/v1';

import { queryClient } from 'utils/tanstack';

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const [authMessage, setAuthMessage] = useState<string>();

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string>();
  const refreshUser = useCallback(() => refreshIdentity(true), []);

  useEffect(() => {
    let generation = sessionAuth.read()?.generation;
    let lastToken: string | undefined;
    let mounted = true;
    const dispatchSession = (tokens: SessionTokens, profile = currentIdentity()) => {
      const claims = jwtDecode<UserProfile>(tokens.token);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          session: tokens,
          user: {
            ...claims,
            permissions: [],
            company_role_id: undefined,
            company_role_label: undefined,
            ...profile,
          },
        },
      });
    };
    const unsubscribeIdentity = subscribeIdentity((profile, error) => {
      if (!mounted) return;
      const tokens = sessionAuth.read();
      if (!tokens) return;
      dispatchSession(tokens, profile);
      setProfileLoading(false);
      setProfileError(error ? 'Account permissions could not be refreshed. Company management is unavailable until you retry.' : undefined);
    });
    const update = (tokens: SessionTokens | null, message?: string) => {
      const nextGeneration = sessionAuth.read()?.generation;
      const changedAccount = nextGeneration !== generation;
      if (changedAccount || !tokens) {
        queryClient.clear();
        clearIdentity();
      }
      generation = nextGeneration;
      setAuthMessage(message);
      if (tokens) {
        try {
          dispatchSession(tokens);
          if (lastToken !== tokens.token || changedAccount) {
            lastToken = tokens.token;
            if (!currentIdentity() || changedAccount) setProfileLoading(true);
            void refreshIdentity().catch(() => {});
          }
          return;
        } catch {
          sessionAuth.clear('Please sign in again.');
          return;
        }
      }
      lastToken = undefined;
      setProfileLoading(false);
      setProfileError(undefined);
      dispatch({ type: LOGOUT });
    };
    const refreshOnReturn = () => {
      if (sessionAuth.read() && document.visibilityState === 'visible') void refreshIdentity().catch(() => {});
    };
    const unsubscribe = subscribeSession(update);
    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);
    sessionAuth.sync();
    return () => {
      mounted = false;
      unsubscribe();
      unsubscribeIdentity();
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', refreshOnReturn);
    };
  }, []);

  const login = async (email: string, password: string, recaptcha_token_v3: string, recaptcha_token_v2: string) => {
    await api.login({ email, password, recaptcha_token_v3, recaptcha_token_v2 });
  };

  const googleLogin = async (id_token: string) => {
    await api.googleLogin({ id_token });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    // todo: this flow need to be recode as it not verified
    // const id = chance.bb_pin();
    // const response = await axios.post('/api/account/register', {
    //   id,
    //   email,
    //   password,
    //   firstName,
    //   lastName
    // });
    // let users = response.data;
    // if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
    //   const localUsers = window.localStorage.getItem('users');
    //   users = [
    //     ...JSON.parse(localUsers!),
    //     {
    //       id,
    //       email,
    //       password,
    //       name: `${firstName} ${lastName}`
    //     }
    //   ];
    // }
    // window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    const pending = api.logout();
    const signedOutRevision = sessionAuth.snapshot();
    try {
      await pending;
    } catch {
      if (sessionAuth.snapshot() !== signedOutRevision) return;
      setAuthMessage('Signed out on this browser. Server revocation could not be confirmed; the session may still be valid on the server.');
    }
  };

  const forgotPassword = async (email: string, recaptcha_token_v3: string, recaptcha_token_v2: string) => {
    await api.forgotPassword({ email, recaptcha_token_v3, recaptcha_token_v2 });
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        authMessage,
        profileLoading,
        profileError,
        refreshUser,
        can: permission => hasCompanyPermission(state.user, permission),
        login,
        googleLogin,
        logout,
        register,
        forgotPassword,
        updateProfile,
        isAdmin: state?.user?.role === 'admin' || false,
        isCustomer: state?.user?.role === 'customer' || false,
        isPentester: state?.user?.role === 'pentester' || false,
      }}
    >
      {profileError && state.isLoggedIn && (
        <Alert severity="warning" action={<Button onClick={() => refreshUser().catch(() => {})}>Retry</Button>}>
          {profileError}
        </Alert>
      )}
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
